import React from 'react';
import { ArrowRight, GitMerge, History, Eye, Settings2, LayoutDashboard, FolderLock, BarChart3, ShieldCheck, HeartPulse, Banknote, Zap, Building2, CheckCircle2, Lock, Globe, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import DemoVideo from '../components/DemoVideo';
import { Eyebrow, SectionTitle, GridBg, GlowOrb } from '../components/SectionHeader';
import { frameworks, productStats, capabilities, platformFeatures, targetIndustries, securityBadges } from '../mock';

const iconMap = { GitMerge, History, Eye, Settings2, LayoutDashboard, FolderLock, BarChart3, ShieldCheck, HeartPulse, Banknote, Zap, Building2 };
const badgeIcons = [ShieldCheck, Lock, Globe, Activity];

const Products = ({ onContact }) => {
  return (
    <div className="bg-[#0a0e1a]">
      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <GridBg />
        <GlowOrb className="w-[600px] h-[600px] -top-32 -left-32" />
        <GlowOrb className="w-[500px] h-[500px] top-10 right-0" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-300 text-xs font-medium">
            <ShieldCheck className="w-3.5 h-3.5" /> IronVision AI
          </span>
          <h1 className="mt-6 text-5xl md:text-6xl font-bold text-white leading-tight tracking-tight max-w-4xl">
            Compliance, Reimagined as <span className="text-cyan-400">Real-Time Intelligence</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-3xl leading-relaxed">
            IronVision AI is a next-generation Governance, Risk, and Compliance (GRC) platform that transforms compliance from a reactive, manual process into a real-time, intelligence-driven capability.
          </p>
          <p className="mt-4 text-base text-slate-400 max-w-3xl leading-relaxed">
            Organizations today struggle to manage overlapping requirements across frameworks like NIST, CMMC, HIPAA, ISO 27001, and GDPR while simultaneously proving compliance and maintaining security. IronVision AI solves this challenge by automatically mapping policies, technical controls, and evidence across multiple frameworks, continuously monitoring compliance posture, and identifying risks before they become audit findings or security incidents.
          </p>
          <div className="mt-10">
            <Button onClick={onContact} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold h-12 px-7 rounded-md shadow-[0_0_28px_rgba(34,211,238,0.4)]">
              Request Demo <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Framework pills */}
          <div className="mt-14">
            <p className="text-xs tracking-[0.3em] text-slate-500 uppercase mb-4">Supported Frameworks</p>
            <div className="flex flex-wrap gap-3">
              {frameworks.map(f => (
                <div key={f.name} className="px-4 py-2.5 rounded-lg border border-cyan-400/15 bg-slate-900/40 backdrop-blur-sm hover:border-cyan-400/40 transition flex items-center gap-3">
                  <span className="text-cyan-300 font-mono text-sm">{f.name}</span>
                  <span className="text-slate-500 text-xs">{f.version}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 border-y border-cyan-400/10 bg-[#070b15]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {productStats.map(s => (
            <div key={s.label} className="text-center">
              <p className="text-5xl md:text-6xl font-bold text-cyan-400">{s.value}</p>
              <p className="mt-2 text-sm text-slate-400 tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DEMO VIDEO */}
      <section data-testid="product-demo-section" className="py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <Eyebrow>Product Demo</Eyebrow>
            <SectionTitle>See IronVision AI <span className="text-cyan-400">in Action</span></SectionTitle>
            <p className="mt-5 text-slate-400 max-w-2xl mx-auto">Watch how IronVision AI brings human-approved AI to the NIST Risk Management Framework (RMF) process.</p>
          </div>
          <DemoVideo src="/videos/ironvision-ai-rmf-demo.mp4" poster="/videos/ironvision-ai-rmf-demo-poster.jpg" onContact={onContact} />
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Eyebrow>Core Capabilities</Eyebrow>
            <SectionTitle>Built for Compliance <span className="text-cyan-400">Efficiency</span></SectionTitle>
            <p className="mt-5 text-slate-400 max-w-2xl mx-auto">Streamline your GRC operations with intelligent automation and unified visibility.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map(c => {
              const Icon = iconMap[c.icon];
              return (
                <div key={c.title} className="rounded-2xl border border-cyan-400/10 bg-slate-900/40 p-7 hover:border-cyan-400/40 hover:-translate-y-1 transition duration-300">
                  <div className="w-12 h-12 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                    {Icon && <Icon className="w-6 h-6 text-cyan-400" />}
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-white">{c.title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-28 bg-[#080c17] border-y border-cyan-400/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Eyebrow>How It Works</Eyebrow>
            <SectionTitle>Framework Crosswalk <span className="text-cyan-400">Intelligence</span></SectionTitle>
            <p className="mt-5 text-slate-400 leading-relaxed">Our pre-built crosswalk mappings automatically identify overlapping controls across frameworks, reducing the time and effort required to maintain multiple compliance certifications.</p>
            <ul className="mt-7 space-y-3">
              {['Map one control to satisfy multiple frameworks', 'Track control gaps across all certifications', 'Reduce evidence collection duplication', 'Maintain version-controlled framework updates'].map(b => (
                <li key={b} className="flex gap-3 text-slate-200"><CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" /><span>{b}</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-slate-900/80 to-slate-950/60 p-8 shadow-[0_0_60px_rgba(34,211,238,0.08)]">
            <div className="flex items-center justify-between mb-5">
              <p className="text-xs tracking-[0.25em] text-cyan-400 font-semibold">CONTROL: AC-2</p>
              <span className="px-2 py-1 text-[10px] rounded bg-cyan-400/10 text-cyan-300 border border-cyan-400/20">ACTIVE</span>
            </div>
            <p className="text-xl font-semibold text-white mb-6">Account Management</p>
            <div className="space-y-3">
              {[{f:'NIST 800-53', c:'AC-2'}, {f:'SOC 2', c:'CC6.1'}, {f:'ISO 27001', c:'A.9.2.1'}].map(m => (
                <div key={m.f} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/40 border border-slate-700/50">
                  <span className="text-slate-200 text-sm">{m.f}</span>
                  <span className="font-mono text-cyan-300 text-sm">{m.c}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-slate-800">
              <p className="text-xs tracking-[0.25em] text-fuchsia-400 font-semibold mb-2">CROSSWALK MAPPING</p>
              <p className="text-slate-300 text-sm">One implementation satisfies <span className="text-cyan-400 font-semibold">3 framework</span> requirements</p>
            </div>
          </div>
        </div>
      </section>

      {/* FRAMEWORKS */}
      <section className="py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Eyebrow>Supported Frameworks</Eyebrow>
            <SectionTitle>Complete Framework <span className="text-cyan-400">Coverage</span></SectionTitle>
            <p className="mt-5 text-slate-400 max-w-2xl mx-auto">Out-of-the-box support for major compliance frameworks with continuously updated control libraries.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {frameworks.map(f => (
              <div key={f.name} className="rounded-2xl border border-cyan-400/10 bg-slate-900/40 p-7 hover:border-cyan-400/40 transition">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-2xl font-bold text-white">{f.name}</h3>
                  <span className="text-xs text-slate-500">{f.version}</span>
                </div>
                <p className="mt-3 text-cyan-400 text-sm font-mono">{f.controls}</p>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM FEATURES */}
      <section className="py-28 bg-[#080c17] border-y border-cyan-400/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Eyebrow>Platform Features</Eyebrow>
            <SectionTitle>Everything You <span className="text-cyan-400">Need</span></SectionTitle>
            <p className="mt-5 text-slate-400 max-w-2xl mx-auto">Comprehensive tools for multi-framework compliance management.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {platformFeatures.map(p => {
              const Icon = iconMap[p.icon];
              return (
                <div key={p.title} className="rounded-2xl border border-cyan-400/10 bg-slate-900/40 p-8 hover:border-cyan-400/30 transition">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                      {Icon && <Icon className="w-5 h-5 text-cyan-400" />}
                    </div>
                    <h3 className="text-xl font-semibold text-white">{p.title}</h3>
                  </div>
                  <p className="mt-4 text-sm text-slate-400 leading-relaxed">{p.description}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.bullets.map(b => (
                      <span key={b} className="text-xs px-3 py-1 rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/20">{b}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TARGET INDUSTRIES */}
      <section className="py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Eyebrow>Target Industries</Eyebrow>
            <SectionTitle>Built for <span className="text-cyan-400">Regulated Industries</span></SectionTitle>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {targetIndustries.map(t => {
              const Icon = iconMap[t.icon];
              return (
                <div key={t.title} className="rounded-xl border border-cyan-400/10 bg-slate-900/40 p-6 text-center hover:border-cyan-400/40 hover:-translate-y-1 transition duration-300">
                  <div className="w-12 h-12 mx-auto rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                    {Icon && <Icon className="w-6 h-6 text-cyan-400" />}
                  </div>
                  <h3 className="mt-4 text-sm font-semibold text-white">{t.title}</h3>
                  <p className="mt-2 text-xs text-slate-400 leading-relaxed">{t.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="py-28 bg-[#080c17] border-t border-cyan-400/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow>Security First</Eyebrow>
            <SectionTitle>Enterprise-Grade <span className="text-cyan-400">Security</span></SectionTitle>
            <p className="mt-5 text-slate-400 leading-relaxed">We practice what we preach. IronVision is <span className="text-cyan-300 font-semibold">CMMC Level 2 certified</span> and holds a <span className="text-cyan-300 font-semibold">Top Secret facility clearance</span>—our GRC Platform is built on the same security-first architecture we deliver to federal clients.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {securityBadges.map((b, i) => {
                const Icon = badgeIcons[i];
                return (
                  <div key={b} className="flex items-center gap-3 p-4 rounded-lg border border-cyan-400/10 bg-slate-900/30">
                    <Icon className="w-5 h-5 text-cyan-400" />
                    <span className="text-sm text-slate-200">{b}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rounded-2xl border border-cyan-400/15 bg-gradient-to-br from-slate-900/60 to-slate-950/30 p-8">
            <h3 className="text-xl font-bold text-white">Security Documentation</h3>
            <p className="mt-3 text-slate-400 text-sm leading-relaxed">Request our security documentation including CMMC and facility clearance verification, penetration test results, and security questionnaire responses.</p>
            <Button onClick={onContact} className="mt-6 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold rounded-md h-11 px-6">
              Request Security Docs <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 relative overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] top-0 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
            See IronVision GRC Platform in <span className="text-cyan-400">Action</span>
          </h2>
          <p className="mt-6 text-slate-300 max-w-2xl mx-auto">Schedule a personalized demo to see how our platform can transform your compliance operations.</p>
          <Button onClick={onContact} className="mt-10 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold h-12 px-8 rounded-md shadow-[0_0_28px_rgba(34,211,238,0.4)]">
            Request Demo <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Products;
